import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { applyActionCode, getAuth, isSignInWithEmailLink } from 'firebase/auth';

export const AuthComplete: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const auth = getAuth();
        const href = window.location.href;
        const url = new URL(href);
        const mode = url.searchParams.get('mode');
        const oobCode = url.searchParams.get('oobCode');

        const redirectHome = (delay = 1500) => {
            setTimeout(() => navigate('/'), delay);
        };

        const ensureFreshVerificationSession = async (): Promise<{ verified: boolean; attempts: number }> => {
            const current = auth.currentUser;
            if (!current) {
                return { verified: false, attempts: 0 };
            }

            let verified = current.emailVerified;
            let attempts = 0;

            for (let attempt = 0; attempt < 3; attempt++) {
                attempts = attempt + 1;
                try {
                    await current.reload();
                    verified = current.emailVerified;
                } catch (reloadErr) {
                    console.warn('Unable to reload user after verification:', reloadErr);
                }

                try {
                    await current.getIdToken(true);
                } catch (tokenErr) {
                    console.warn('Unable to force token refresh after verification:', tokenErr);
                }

                if (current.emailVerified) {
                    verified = true;
                    break;
                }

                await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)));
            }

            try {
                localStorage.setItem('forceTokenRefresh', Date.now().toString());
            } catch (storageErr) {
                console.warn('Unable to store forceTokenRefresh flag:', storageErr);
            }

            return { verified, attempts };
        };

        const finish = async () => {
            try {
                // 1) Email verification
                if (oobCode && mode === 'verifyEmail') {
                    try {
                        await applyActionCode(auth, oobCode);
                        const { verified, attempts } = await ensureFreshVerificationSession();
                        localStorage.removeItem('pendingPasswordForLink');
                        localStorage.removeItem('pendingPasswordEmail');
                        if (verified) {
                            setInfo('Email verified! Redirecting…');
                            setLoading(false);
                            redirectHome(800);
                            return;
                        }
                        setError(attempts >= 3
                            ? 'Email verified, but your session did not refresh automatically. Please reopen the link or sign in again.'
                            : 'Email verified! You can now sign in.');
                        setLoading(false);
                        redirectHome(1500);
                        return;
                    } catch (err) {
                        const code = (err as { code?: string }).code;
                        if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') {
                            // Link già usato/gestito; considera verificato e reindirizza.
                            setInfo('Your email is already verified. You can sign in now.');
                            setLoading(false);
                            redirectHome();
                            return;
                        }
                        setError('Verification link invalid or expired. Please request a new one.');
                        setLoading(false);
                        return;
                    }
                }

                // 2) Magic link sign-in
                const isEmailLink = isSignInWithEmailLink(auth, href);
                if (isEmailLink) {
                    let email: string | null = localStorage.getItem('emailForSignIn');
                    if (!email) {
                        email = window.prompt('Enter your email to complete sign in:') || null;
                    }
                    if (!email) {
                        setError('Email required to complete sign in.');
                        setLoading(false);
                        return;
                    }
                    try {
                        const user = await authService.signInWithEmailLink(email, href);
                        await user.getIdToken(true); // refresh claims after sign-in
                        localStorage.setItem('forceTokenRefresh', Date.now().toString());
                        if (!user.emailVerified) {
                            try {
                                await user.reload();
                                await user.getIdToken(true);
                            } catch (reloadErr) {
                                console.warn('Unable to refresh user after magic link:', reloadErr);
                            }
                        }
                        const pendingPassword = localStorage.getItem('pendingPasswordForLink');
                        const pendingEmail = localStorage.getItem('pendingPasswordEmail');
                        if (pendingPassword && pendingEmail && user.email && user.email.toLowerCase() === pendingEmail.toLowerCase()) {
                            try {
                                await authService.setPasswordForCurrentUser(pendingPassword);
                                setInfo('Password set successfully. You can now use email & password to sign in.');
                            } catch (setErr) {
                                console.warn('Could not set password after magic link:', setErr);
                                setInfo('Signed in. Could not set the new password automatically, please try again.');
                            } finally {
                                localStorage.removeItem('pendingPasswordForLink');
                                localStorage.removeItem('pendingPasswordEmail');
                            }
                        } else {
                            localStorage.removeItem('pendingPasswordForLink');
                            localStorage.removeItem('pendingPasswordEmail');
                        }
                        setInfo('Sign in completed! Redirecting...');
                        setLoading(false);
                        redirectHome();
                        return;
                    } catch (err) {
                        const code = (err as { code?: string }).code;
                        if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') {
                            setError('This sign-in link is invalid or expired. Please request a new magic link.');
                        } else {
                            setError('Unable to complete sign in. Please try again.');
                        }
                        setLoading(false);
                        return;
                    }
                }

                // 3) Nessuna azione riconosciuta
                setInfo('Nothing to do. Redirecting...');
                setLoading(false);
                redirectHome();
            } catch (err) {
                console.error('AuthComplete unexpected error:', err);
                setError('Unable to complete the requested action. Please try again.');
                setLoading(false);
            }
        };

        finish();
    }, [navigate]);

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', fontFamily: 'Segoe UI, Arial, sans-serif' }}>
                <div style={{ background: 'white', padding: '32px 40px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', textAlign: 'center', minWidth: '300px' }}>
                    <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #357a38', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }}></div>
                    <p style={{ margin: 0, fontSize: '16px', color: '#333' }}>Processing…</p>
                </div>
                <style>{`@keyframes spin {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
            </div>
        );
    }

    const handleReturn = () => navigate('/');

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', fontFamily: 'Segoe UI, Arial, sans-serif' }}>
            <div style={{ background: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', textAlign: 'center', minWidth: '320px' }}>
                <div style={{ width: '60px', height: '60px', backgroundColor: error ? '#fdecea' : '#e8f5e8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '30px', color: error ? '#d93025' : '#357a38' }}>
                    {error ? '!' : '✓'}
                </div>
                <h2 style={{ color: error ? '#d93025' : '#357a38', margin: '0 0 10px 0' }}>
                    {error ? 'Action failed' : 'Action completed'}
                </h2>
                <p style={{ color: '#666', margin: '0 0 20px 0' }}>
                    {error || info || 'All done.'}
                </p>
                <button
                    onClick={handleReturn}
                    style={{ backgroundColor: '#357a38', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '6px', fontSize: '16px', cursor: 'pointer' }}
                >
                    Return to login
                </button>
            </div>
        </div>
    );
};
